import i18next from "../../locales/i18next";

/* eslint-disable no-undef */
function LineChart(datasets, {
  y = (data) => data.close,
  y2 = (data) => data.profit,
  x = (data) => data.date,
  x2 = (data) => data.end_date,
  defined, // for gaps in data
  curve = d3.curveLinear, // method of interpolation between points
  marginTop = 40, // top margin, in pixels
  marginRight = 80, // right margin, in pixels
  marginBottom = 30, // bottom margin, in pixels
  marginLeft = 80, // left margin, in pixels
  width, // outer width, in pixels
  height, // outer height, in pixels
  xType = d3.scaleUtc, // type of x-scale
  xDomain, // [xmin, xmax]
  xRange = [marginLeft, width - marginRight], // [left, right]
  yType = d3.scaleLinear, // type of y-scale
  yDomain, // [ymin, ymax]
  y2Domain, // [y2min, y2max]
  yRange = [height - marginBottom, marginTop], // [bottom, top]
  y2Range = [height - marginBottom, (height - marginBottom) / 2], // [bottom, top]
  chartBgColor = "#222",
  lineColor = '#0D70D5',
  barActColor = '#87CEFA',
  barEstColor = 'orange',
  barMinusColor = 'pink',
  axisStringColor = '#fff',
  axisFontSize = '14px',
  strokeWidth = 1.5, // stroke width of line, in pixels
  strokeLinejoin = "round", // stroke line join of line
  strokeLinecap = "round", // stroke line cap of line
  yFormat, // a format specifier string for the y-axis
  yLabel, // a label for the y-axis
  y2Label
} = {}) {
  /** X軸のみのデータ配列(株価日付) @type {String[]} */
  const X = d3.map(datasets.stock, x);
  /** X軸のみのデータ配列(利益日付) @type {String[]} */
  const X2 = d3.map(datasets.profit, x2);
  /** Y軸のみのデータ配列(終値) @type {Number[]} */
  const Y = d3.map(datasets.stock, y);

  /** Y軸2のデータ配列(profitの値) @type {Number[]} */
  const Y2 = d3.map(datasets.profit, y2);
  /** indexのみのデータ配列[1, 2 ... 100, 101] @type {Number[]} */
  const I = d3.map(datasets.stock, (_, i) => i);

  // defined = datasetの中で値にNaNが含まれていないものだけを判定する
  // _ => {date, close}, i => d3.mapでのindex
  if (defined === undefined) defined = (_, i) => !isNaN(X[i]) && !isNaN(Y[i]);

  /** defined変数内に保存された、日付と終値のどちらもデータが存在するもののみ集めた配列 @type {(string | number)[]} */
  const D = d3.map(datasets.stock, defined);

  /** 最小値が０以上の場合は基準値を０とする(負の数値の時のみ0以下を基準線とする) */
  const checkMin = (minNum) => (minNum < 0) ? minNum : 0;

  // domainに最大最小値を代入
  if (xDomain === undefined) xDomain = [d3.min(X), d3.max([d3.max(X2), d3.max(X)])];
  // const minY = d3.min([d3.min(Y), d3.min(TMIN)]);
  const minY = d3.min(Y);
  // const maxY = d3.max([d3.max(Y), d3.max(TMAX)]);
  const maxY = d3.max(Y);
  // 株価と目標株価の中で最小値を抜き出し、さらにそこから−10％したものをグラフの最小値とする。（-10％するのは軸に値がくっつかないようにするため）
  if (yDomain === undefined) yDomain = [minY * 0.5, maxY * 1.1];
  if (y2Domain === undefined) y2Domain = [checkMin(d3.min(Y2)), d3.max(Y2)];

  // domainでグラフの最大最小値、rangeで描画範囲を決定
  const xScale = xType(xDomain, xRange);
  const yScale = yType(yDomain, yRange);
  const y2Scale = yType(y2Domain, y2Range);
  const xAxis = d3.axisBottom(xScale).ticks(width / 100).tickSizeOuter(0);
  const yAxis = d3.axisLeft(yScale).ticks(height / 80, yFormat);
  const y2Axis = d3.axisRight(y2Scale).ticks(height / 80, yFormat);

  // lineを描画
  const stockLine = d3.line()
    .defined(i => D[i])
    .curve(curve)
    .x(i => xScale(X[i]))
    .y(i => yScale(Y[i]));

  // グラフの大元svgを作成
  const svg = d3.create("svg")
    .attr("width", width)
    .attr("height", height)
    .attr("id", "stock-chart-svg")
    .attr("style", `max-width: 100%; background-color: ${chartBgColor};`)
    .attr("font-size", 12)
    .attr("color", axisStringColor)
    .style("-webkit-tap-highlight-color", "transparent");

  // y軸2(右)を表示
  svg.append("g")
    .attr("transform", `translate(${xRange[1]},0)`)
    .call(y2Axis)
    .style("font-size", axisFontSize)
    .call(g => g.select(".domain").remove())
    .call(g => g.append("text")
      .attr("x", marginRight)
      .attr("y", 10)
      .attr("text-anchor", "end")
      .text(y2Label)
      .style("fill", axisStringColor)
      .attr("dx", -5)
      .attr("dy", 5));

  // 株価チャートを表示
  svg.append("path")
    .attr("fill", "none")
    .attr("stroke", lineColor)
    .attr("stroke-width", strokeWidth)
    .attr("stroke-linejoin", strokeLinejoin)
    .attr("stroke-linecap", strokeLinecap)
    .attr("d", stockLine(I));

    // 株価チャートの歯抜け部分を表示
    svg.append("path")
      .attr("fill", "none")
      .attr("stroke", lineColor)
      .attr("d", stockLine(I.filter(i => D[i])));

  const barTooltip = d3.select("body").append("div").attr("class", "bar-tooltip").style("pointer-events", "none");
  const timeFormat = d3.timeFormat("%y.%m");
  const barPointerMoved = (e, data) => {
    barTooltip.style("visibility", "visible")
      .html(`<p>FY${timeFormat(data.end_date)}</p><span>${i18next.t(`embed.sc.profitType.${data.profit_type}`)}: ${data.profit}</span>`)
      .style("top", (e.pageY - 60) + "px")
      .style("left", (e.pageX - 110) + "px");
  };

  // 利益グラフを表示
  svg.append("g")
    .attr("fill", "none")
    .attr("fill-opacity", 0.5)
    .selectAll("rect")
    .data(datasets.profit)
    .join("rect")
      .attr("x", (d) => xScale(d.start_date))
      .attr("width", d => xScale(d.end_date) - xScale(d.start_date))
      .attr("y", d => {
        return (d3.min(Y2) < 0) ? y2Scale(Math.max(0, d.profit)) : y2Scale(d.profit);
      })
      .attr("class", "profit-bar")
      .attr("height", d => {
        return (d3.min(Y2) < 0) ? Math.abs(y2Scale(d.profit) - y2Scale(0)) : y2Scale(0) - y2Scale(d.profit);
      })
      .attr("fill", d => {
        if (d.type === 'form') return barEstColor;

        return (d.profit < 0) ? barMinusColor : barActColor;
      })
      .attr('opacity', '0.7')
    .on("pointerenter pointermove", barPointerMoved)
    .on("pointerleave", ()=> {
      barTooltip.style("visibility", "hidden");
    });

  // x軸にpeバンドが描画されないよう、一枚レイヤーを重ねる
  svg.append("rect")
    .attr("x", marginLeft)
    .attr("y", height - marginBottom)
    .attr("width", width - marginRight - marginLeft)
    .attr("height","100%")
    .attr("fill", chartBgColor);
  // Y軸にグラフが重ならないよう、一枚レイヤーを重ねる
  svg.append("rect")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", marginLeft)
    .attr("height","100%")
    .attr("fill", chartBgColor);
  // x軸を描画
  svg.append("g")
    .attr("transform", `translate(0,${height - marginBottom})`)
    .call(xAxis)
    .style("font-size", axisFontSize);
  // y軸を描画
  svg.append("g")
    .attr("transform", `translate(${marginLeft},0)`)
    .call(yAxis)
    .style("font-size", axisFontSize)
    .call(g => g.select(".domain").remove())
    .call(g => g.selectAll(".tick line").clone()
      .attr("x2", width - marginLeft - marginRight)
      .attr("stroke-opacity", 0.1))
    .call(g => g.append("text")
      .attr("x", -marginLeft)
      .attr("y", 10)
      .attr("fill", "currentColor")
      .attr("text-anchor", "start")
      .text(yLabel)
      .attr("dx", 5)
      .attr("dy", 5));

  return Object.assign(svg.node(), {value: null});
}

{
  // d3のX軸日付用にデータをparse
  const dateTimeParser = d3.timeParse("%Y/%m/%d %H:%M");
  const dateParser = d3.timeParse("%Y/%m/%d");

  const setDataset = (data, beforeDataset)=> {
    const dataset = data.stock_chart.stock_values.map(data => {
      return { 
        date: dateTimeParser(data.date),
        close: data.close || Number.NaN,
      };
    });

    const profitDataset = data.stock_chart.bar_dataset.map(data => {
      return { 
        start_date: dateParser(data.start_date),
        end_date: dateParser(data.end_date),
        profit: data.value,
        profit_type: data.bar_type,
        type: data.type
      };
    });

    const get_est_profit = () => {
      if (!beforeDataset) return [];

      return beforeDataset.profit.filter(b=> b.type === 'form');
    };

    return {
      stock: dataset,
      profit: profitDataset.concat(get_est_profit()),
      profitType: data.stock_chart.profit_type
    };
  };

  const datasets = setDataset(gon);

  const wrapperElem = document.getElementById('stock-chart-wrapper');

  const deleteChart = () => {
    document.getElementById('stock-chart-svg')?.remove();
    document.querySelector('.bar-tooltip')?.remove();
  };

  const calcMargin = (width) => {
    if (width < 500) {
      return { top: 40, right: 40, bottom: 30, left: 50 };
    } else {
      return { top: 40, right: 80, bottom: 30, left: 80 };
    }
  };

  const calcFontSize = (width) => {
    return width < 500 ? '10px' : '14px';
  };

  const renderChart = (datasets)=> {
    const baseDatasets = datasets;
    const width = wrapperElem.clientWidth;
    const margin = calcMargin(width);
    const height = wrapperElem.clientHeight;
    const y2label = baseDatasets.profitType != null ? `${i18next.t(`embed.sc.profitType.${baseDatasets.profitType}`)}${i18next.t('embed.sc.yAxis.billion')}` : '';
    const stockChart = LineChart(baseDatasets, {
      yLabel: i18next.t('embed.sc.yAxis.label'),
      y2Label: y2label,
      width: width,
      height: height,
      marginTop: margin.top,
      marginRight: margin.right,
      marginBottom: margin.bottom,
      marginLeft: margin.left,
      axisFontSize: calcFontSize(width)
    });
    wrapperElem.append(stockChart);
  };

  renderChart(datasets);

  const resize = ()=> {
    deleteChart();
    renderChart(datasets);
  };

  window.addEventListener("resize", resize);
}
/* eslint-enable no-undef */
